'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import React from 'react';
import { ArrowsOut, X, Play, Pause, Gauge, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useProductTelemetryOnce } from '@/hooks/use-product-telemetry-once';
import { captureProductTelemetry } from '@/lib/product-telemetry';
import { VisualizationLoader } from '@/components/visualizations/visualization-loader';

const SPEED_OPTIONS = [0.25, 0.5, 1, 1.5, 2] as const;
import dynamic from 'next/dynamic';

interface P5RendererProps {
  setup: string;
  draw?: string;
  mousePressed?: string;
  keyPressed?: string;
  visualizationId?: string;
  displayContext?: 'inline' | 'artifact';
  renderActions?: React.ReactNode;
  onRetryError?: (info: { toolName: string; error: string; code: string }) => void;
}

// Dynamic import for p5 (client-side only)
const P5Wrapper = dynamic(() => import('./p5-wrapper'), {
  ssr: false,
  loading: () => (
    <VisualizationLoader kind="animation" title="Loading animation" className="h-full my-0 rounded-none border-0 shadow-none" />
  ),
});

function CompactIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClick}
          className="h-8 w-8 px-0"
          aria-label={label}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function P5Renderer({
  setup,
  draw,
  mousePressed,
  keyPressed,
  visualizationId,
  displayContext = 'inline',
  renderActions,
  onRetryError,
}: P5RendererProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speed, setSpeed] = useState(1);
  // Bumping this key remounts P5Wrapper, restarting the sketch from setup().
  const [runKey, setRunKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [chatWidth, setChatWidth] = useState(0);
  const [fullscreenViewportSize, setFullscreenViewportSize] = useState({ width: 0, height: 0 });
  const [measuredCanvasSize, setMeasuredCanvasSize] = useState<{ width: number; height: number } | null>(null);
  const [canvasTone, setCanvasTone] = useState<{ backgroundColor: string; isDark: boolean } | null>(null);
  const normalViewportRef = useRef<HTMLDivElement>(null);
  const fullscreenViewportRef = useRef<HTMLDivElement>(null);
  const renderStartRef = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const hasTrackedReadyRef = useRef(false);
  const canvasSize = measuredCanvasSize;
  const surfaceColor = canvasTone?.isDark ? canvasTone.backgroundColor : '#0f172a';

  useProductTelemetryOnce(
    'visualization_render_failed',
    Boolean(error),
    {
      tool_name: 'p5',
      failure_kind: 'runtime-render-error',
      visualization_id: visualizationId,
      display_context: displayContext,
    },
    error ? `${visualizationId ?? 'p5'}:${error}` : undefined,
  );

  useEffect(() => {
    renderStartRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    hasTrackedReadyRef.current = false;
  }, [displayContext, setup, draw, mousePressed, keyPressed]);

  useEffect(() => {
    if (!measuredCanvasSize || hasTrackedReadyRef.current) return;

    hasTrackedReadyRef.current = true;
    captureProductTelemetry('visualization_render_ready', {
      tool_name: 'p5',
      visualization_id: visualizationId,
      display_context: displayContext,
      render_duration_ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - renderStartRef.current),
      canvas_width: measuredCanvasSize.width,
      canvas_height: measuredCanvasSize.height,
    });
  }, [displayContext, measuredCanvasSize, visualizationId]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  const restart = useCallback(() => {
    setIsPaused(false);
    setRunKey(prev => prev + 1);
  }, []);

  const handleError = useCallback((err: string) => {
    setError(err);
  }, []);

  useEffect(() => {
    const normalEl = normalViewportRef.current;
    const fullscreenEl = fullscreenViewportRef.current;

    const updateNormal = () => {
      if (!normalEl) return;
      const rect = normalEl.getBoundingClientRect();
      setChatWidth(rect.width);
    };

    const updateFullscreen = () => {
      if (!fullscreenEl) return;
      const rect = fullscreenEl.getBoundingClientRect();
      setFullscreenViewportSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateNormal();
    updateFullscreen();

    const resizeObserver = new ResizeObserver(() => {
      updateNormal();
      updateFullscreen();
    });

    if (normalEl) resizeObserver.observe(normalEl);
    if (fullscreenEl) resizeObserver.observe(fullscreenEl);

    window.addEventListener('resize', updateNormal);
    window.addEventListener('resize', updateFullscreen);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateNormal);
      window.removeEventListener('resize', updateFullscreen);
    };
  }, [isFullscreen]);

  const chatScale = useMemo(() => {
    if (!chatWidth || !canvasSize) return 1;
    const availableWidth = Math.max(chatWidth - 24, 1);
    return Math.min(availableWidth / canvasSize.width, 1);
  }, [canvasSize, chatWidth]);

  const fullscreenScale = useMemo(() => {
    if (!fullscreenViewportSize.width || !canvasSize) return 1;
    const availableWidth = Math.max(fullscreenViewportSize.width - 32, 1);
    if (!fullscreenViewportSize.height) {
      return availableWidth / canvasSize.width;
    }
    const availableHeight = Math.max(fullscreenViewportSize.height - 32, 1);
    return Math.min(availableWidth / canvasSize.width, availableHeight / canvasSize.height);
  }, [canvasSize, fullscreenViewportSize.height, fullscreenViewportSize.width]);

  if (error) {
    const fullCode = [setup, draw, mousePressed, keyPressed].filter(Boolean).join('\n\n');
    return (
      <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-destructive">Animation Error</p>
          {onRetryError && (
            <button
              onClick={() => onRetryError({ toolName: 'p5', error, code: fullCode })}
              className="shrink-0 px-3 py-1 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
        <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{error}</pre>
      </div>
    );
  }

  const speedControls = (
    <div className="flex items-center gap-1">
      <Gauge weight="bold" className="size-3.5 text-muted-foreground mr-1" />
      {SPEED_OPTIONS.map((s) => (
        <button
          key={s}
          onClick={() => setSpeed(s)}
          className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
            speed === s
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          {s}x
        </button>
      ))}
    </div>
  );

  const renderCanvas = (scale: number) => {
    const intrinsicWidth = canvasSize?.width ?? 1;
    const intrinsicHeight = canvasSize?.height ?? 1;
    const effectiveScale = canvasSize ? scale : 1;
    const displayWidth = Math.max(1, Math.round(intrinsicWidth * effectiveScale));
    const displayHeight = Math.max(1, Math.round(intrinsicHeight * effectiveScale));

    return (
      <div
        style={{
          width: `${displayWidth}px`,
          height: `${displayHeight}px`,
          flex: '0 0 auto',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: canvasSize ? `${intrinsicWidth}px` : 'auto',
            height: canvasSize ? `${intrinsicHeight}px` : 'auto',
            transform: `scale(${effectiveScale})`,
            transformOrigin: 'top left',
          }}
        >
          <P5Wrapper
            key={runKey}
            setup={setup}
            draw={draw}
            mousePressed={mousePressed}
          keyPressed={keyPressed}
          isPaused={isPaused}
          speed={speed}
          onError={handleError}
          onCanvasSizeChange={setMeasuredCanvasSize}
          onCanvasToneChange={setCanvasTone}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Normal view */}
      <div
        className="w-full border rounded-lg overflow-hidden group relative"
        style={{ backgroundColor: surfaceColor }}
      >
        <div className="absolute top-3 right-3 z-10 flex flex-wrap items-center justify-end gap-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
          {renderActions}
          <div className="rounded-md border border-white/10 bg-black/15 px-2 py-1 text-white/90">
            {speedControls}
          </div>
          <CompactIconButton label={isPaused ? 'Play animation' : 'Pause animation'} onClick={togglePause}>
            {isPaused ? (
              <Play weight="bold" className="size-4 text-white" />
            ) : (
              <Pause weight="bold" className="size-4 text-white" />
            )}
          </CompactIconButton>
          <CompactIconButton label="Restart animation" onClick={restart}>
            <ArrowCounterClockwise weight="bold" className="size-4 text-white" />
          </CompactIconButton>
          <CompactIconButton label="Fullscreen" onClick={toggleFullscreen}>
            <ArrowsOut weight="bold" className="size-4 text-white" />
          </CompactIconButton>
        </div>

        <div 
          ref={normalViewportRef}
          className="flex items-start justify-center p-3"
          style={{ backgroundColor: surfaceColor }}
        >
          {renderCanvas(chatScale)}
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div
          className="fixed inset-0 z-50 flex flex-col"
          style={{ backgroundColor: surfaceColor }}
        >
          <div
            className="flex items-center justify-end gap-3 px-6 py-4 shrink-0"
            style={{ backgroundColor: surfaceColor }}
          >
            {renderActions}
            <div className="rounded-md border border-white/10 bg-black/15 px-2 py-1 text-white/90">
              {speedControls}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={togglePause}
              className="border-white/10 bg-black/15 text-white hover:bg-black/25"
            >
              {isPaused ? (
                <Play weight="bold" className="size-4 mr-1" />
              ) : (
                <Pause weight="bold" className="size-4 mr-1" />
              )}
              {isPaused ? 'Play' : 'Pause'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={restart}
              className="border-white/10 bg-black/15 text-white hover:bg-black/25"
            >
              <ArrowCounterClockwise weight="bold" className="size-4 mr-1" />
              Restart
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
              className="border-white/10 bg-black/15 text-white hover:bg-black/25"
            >
              <X weight="bold" className="size-4 mr-1" />
              Close
            </Button>
          </div>
          
          <div
            ref={fullscreenViewportRef}
            className="flex-1 flex items-start justify-center p-6 overflow-auto"
            style={{ backgroundColor: surfaceColor }}
          >
            {renderCanvas(fullscreenScale)}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Renders p5 output from a tool call
 */
export function P5ToolOutput({ output, visualizationId, displayContext = 'inline', renderActions, onRetryError }: {
  output: {
    success: boolean;
    title?: string;
    description?: string;
    setup?: string;
    draw?: string;
    mousePressed?: string;
    keyPressed?: string;
    error?: string;
  };
  visualizationId?: string;
  displayContext?: 'inline' | 'artifact';
  renderActions?: React.ReactNode;
  onRetryError?: (info: { toolName: string; error: string; code: string }) => void;
}) {
  if (!output.success) {
    return (
      <div className="p-4 border border-amber-500/50 rounded-lg bg-amber-500/10">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Animation Error</p>
        <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{output.error}</pre>
      </div>
    );
  }

  if (!output.setup) {
    return null;
  }

  return (
    <P5Renderer
      setup={output.setup}
      draw={output.draw}
      mousePressed={output.mousePressed}
      keyPressed={output.keyPressed}
      visualizationId={visualizationId}
      displayContext={displayContext}
      renderActions={renderActions}
      onRetryError={onRetryError}
    />
  );
}
